import React, { useState } from 'react';
import { X, Calendar, Share2, Copy, Check, Link2 } from 'lucide-react';
import type { Badge, BadgeMicroskill, BadgePathway } from '@/types/achievement';
import { useUser } from '@/contexts/UserContext';

interface BadgeDetailsPopupProps {
  badge: Badge | null;
  isOpen: boolean;
  onClose: () => void;
  onShare?: (badge: Badge) => void;
}

const BadgeDetailsPopup: React.FC<BadgeDetailsPopupProps> = ({ 
  badge, 
  isOpen, 
  onClose, 
  onShare 
}) => {
  const [copied, setCopied] = useState(false);
  const { user } = useUser();

  if (!isOpen || !badge) return null;

  const baseUrl = import.meta.env.VITE_APP_URL || 'https://dev-api.skillsture.io';
  // const encryptedUserId = user?.id ? getEncryptedUserId(user.id.toString()) : '0';
  // const credentialLink = `${baseUrl}/profile/${encryptedUserId}/badges/${badge.id}`;
  // const credentialLink = `${baseUrl}/profile/${user?.id}/badges/${badge.id}`;
  const credentialLink = `${baseUrl}/profile/${user?.id}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(credentialLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleShareLinkedIn = () => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(credentialLink)}`;
    window.open(linkedInUrl, '_blank', 'width=600,height=400');
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    // return date.toLocaleDateString('en-US', {
    //   year: 'numeric',
    //   month: 'long',
    //   day: 'numeric'
    // });
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get category name from API response
  const getCategoryName = (badge: Badge) => {
    return badge?.core_category?.name || badge?.category || 'Unknown Category';
  };

  // Get skillstree name from API response
  const getSkillstreeName = (badge: Badge) => {
    return badge?.skillstree?.skillstree_name || 'Unknown Skillstree';
  };

  // Get microskill name from API response
  const getMicroskillName = (badge: Badge) => {
    return (badge?.microskill as BadgeMicroskill)?.version || badge.microskill as string || (badge?.microskill as BadgeMicroskill)?.code || 'Unknown Microskill';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-blue-950 to-slate-800 border border-blue-300/40 outline-slate-600 rounded-xl max-w-md w-full p-6 relative text-base text-slate-400">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-white/60 hover:text-white bg-transparent transition-colors"
        >
          <X size={24} />
        </button>

        {/* Badge Image & Name */}
        <div className="flex sm:flex-row sm:justify-between flex-col justify-center items-center mb-6">
          <img 
            className="flex h-auto max-w-16 min-w-16 object-center" 
            src={badge.image || badge.image_url || '/pages/achievements/Digital-Badges.png'} 
            alt={badge.name} 
            onError={(e) => e.currentTarget.src = '/pages/achievements/Digital-Badges.png'}
          />
          <div className="flex flex-grow flex-col sm:items-start items-center ps-3">
            <span className="text-lg font-semibold text-white mb-1">{badge.name || getSkillstreeName(badge)}</span>
            <span className="font-sm">
              Achievement Unlocked!
            </span>
          </div>
        </div>
        {/* Badge Details */}
        <div className="space-y-3 my-4">
          {badge.earned_at && <div className="flex items-center">
            <Calendar size={16} className="mr-3 flex-shrink-0" />
            <span>Earned on {formatDate(badge.earned_at)}</span>
          </div>}
          <div className="flex items-start">
            <span className="font-medium mr-2">Microskill:</span>
            <span>{getMicroskillName(badge)}</span>
          </div>
          <div className="flex items-start">
            <span className="font-medium mr-2">Learning Pathway:</span>
            <span>{`Version ${(badge.pathway as BadgePathway)?.version || 1}`}</span>
          </div>
          <div className="flex items-center flex-wrap">
            <span className="font-medium mr-2">Core Category:</span>
            <span className="text-sm font-medium bg-purple-100 text-purple-800 rounded-sm py-0.5 px-2.5">
              {getCategoryName(badge)}
            </span>
          </div>
          {badge.skillstree && (
            <div className="flex items-center flex-wrap">
              <span className="font-medium mr-2">Skillstree:</span>
              <span className="text-sm font-medium bg-blue-100 text-blue-800 rounded-sm py-0.5 px-2.5">
                {getSkillstreeName(badge)}
              </span>
            </div>
          )}
        </div>

        {/* Share Button */}        
        <button
          onClick={handleShareLinkedIn}
          className="w-full flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-lg font-normal transition-colors"
        >
          <Share2 size={20} className="mr-3" />
          Share in LinkedIn
        </button>
        
        {/* Shareable Link */}
        <div className="flex sm:flex-col sm:justify-between flex-col justify-start items-start mt-6 p-4 bg-blue-900/20 rounded outline outline-blue-500/30">
          <span className="block text-blue-300 text-base font-normal mb-2">
            <Link2 size={20} className="inline mr-3 -mt-0.5" />
            Shareable Credential Link
          </span>
          <span className="w-full my-2 text-sm break-words text-wrap font-normal text-blue-200 hover:text-blue-300 transition-colors">
            {credentialLink}
          </span>
          <button
            onClick={handleCopyLink}
            className="w-full mt-2 bg-white rounded-lg outline outline-1 hover:bg-white/70 text-blue-500 py-2 font-medium transition-colors border border-blue-500/30 outline-blue-500/30"
          >
            {copied ? <Check size={20} className="inline mr-3 -mt-1" /> : <Copy size={20} className="inline mr-3 -mt-1" />}
            {copied ? "Link Copied" : "Copy Link"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BadgeDetailsPopup;